import { Button } from 'reactstrap';
import FileSaver from 'file-saver';
import { featureCollection } from '@turf/turf';
import { Feature, FeatureCollection, GeoJsonProperties } from 'geojson';
import { identity } from 'lodash';
import { exportFileName } from '../../../utils/utils';
import { useFarmName } from '../../../utils/hooks';
import { zipExportContent } from '../../../utils/zip';

// Matches the export option values
type SectionFeatureGranularity = 'points' | 'rows' | 'boundary';

export interface ExportSectionFeaturesButtonProps {
  contentType: string;
  extension: string;
  granularity: SectionFeatureGranularity;
  sections: Section[];
  title: string;
  transformFeatures?: (
    featureColl: FeatureCollection,
    section: Section,
  ) => string;
  zipTitle: string;
}

// Non-exhaustive set of section properties
interface Section {
  fieldFeature: Feature;
  id: string;
  name: string;
  points: FeatureCollection;
  rows: FeatureCollection;
}

// Gets the relevant geoJson feature(s) for the section, i.e. the fieldFeature,
// points, or rows. Returns the feature(s) in a FeatureCollection.
export const getSectionFeatures = (
  section: Section,
  granularity: SectionFeatureGranularity,
): FeatureCollection =>
  granularity === 'boundary'
    ? // Get the fieldFeature and wrap in a feature collection
      featureCollection([section.fieldFeature])
    : // Get the corresponding key in the section (points or rows)
      section[granularity];

// Gets the name of the given feature, based on whether it is a boundary, a
// point, or a row
const getFeatureName = (
  granularity: SectionFeatureGranularity,
  row: number, // 0-indexed value
  position: number, // 0-indexed value
  sectionName: string,
) => {
  switch (granularity) {
    case 'points': {
      return `${sectionName}.${row + 1}.${position + 1}`;
    }
    case 'rows':
      return `Row ${row + 1}`;
    default:
      return sectionName;
  }
};

// Gets the properties object for the given feature, removing any undefined
// values or values that should not be exposed to the user
const getFeatureProperties = (
  feature: Feature,
): NonNullable<GeoJsonProperties> => {
  const { properties } = feature;
  if (properties == null) return {};
  return Object.entries(properties).reduce((result, [key, value]) => {
    // Do not include allocationFt or undefined/null properties in export
    // (undefined causes an exception in tokml)
    if (key === 'allocationFt' || value == null) return result;
    return { ...result, [key]: value };
  }, {});
};

// Give every feature a name in the export, and clean up some of the other
// feature properties
const editFeatureProperties = (
  featureColl: FeatureCollection,
  section: Section,
  granularity: SectionFeatureGranularity,
): FeatureCollection => {
  let row = 0;
  let position = 0;
  return featureCollection(
    featureColl.features.map((feature, i) => {
      const properties = getFeatureProperties(feature);
      // Update current row/position if we have moved to a new row
      if (properties.row != null && properties.row !== row) {
        row += 1;
        position = 0;
      }
      // Update row, position, index properties for points and rows
      if (['points', 'rows'].includes(granularity)) {
        properties.row = row + 1; // 1-index the row number
      }
      if (granularity === 'points') {
        properties.position = position + 1;
        properties.index = i;
      }
      // Ensure all features have names in export
      const name = getFeatureName(granularity, row, position, section.name);
      position += 1; // Increment position for next iteration
      return {
        ...feature,
        properties: {
          ...properties,
          name,
        },
      };
    }),
  );
};

const ExportSectionFeaturesButton = ({
  contentType, // MIME type of file, e.g. text/plain
  extension, // Extension for export file, without '.', e.g. 'json'
  granularity,
  sections = [],
  title, // Title for individual export files
  transformFeatures = identity,
  zipTitle = '', // Prefixed to zip file title
  ...buttonProps
}: ExportSectionFeaturesButtonProps) => {
  const farmName = useFarmName();
  const onClick = async () => {
    const contentEntries = sections.map((section) => {
      // Get the data for the export granularity
      const rawFeatureColl = getSectionFeatures(section, granularity);
      // Edit properties object of each feature
      const featureColl = editFeatureProperties(
        rawFeatureColl,
        section,
        granularity,
      );
      const content = transformFeatures(featureColl, section);
      const fileName = `${title} - ${section.name} - ${farmName}`;
      return { title: fileName, content };
    });
    if (sections.length > 1) {
      // Zip files together for single download
      const zipContent = await zipExportContent(contentEntries, extension);
      FileSaver.saveAs(
        zipContent,
        exportFileName(`${zipTitle} ${title} - ${farmName}`, 'zip'),
      );
    } else {
      // Save the single file on its own
      contentEntries.forEach(({ title: fullTitle, content }) => {
        const blob = new Blob([content], { type: contentType });
        FileSaver.saveAs(blob, exportFileName(fullTitle, extension));
      });
    }
  };
  return <Button onClick={onClick} {...buttonProps} />;
};

export default ExportSectionFeaturesButton;
